import type { ReactNode } from 'react'

import { TableCell } from '@/components/ui/table'
import { formatDate } from '@/lib/formatters'
import { COVERAGE_TYPE_LABELS } from '@/models/insuranceCoverage'
import type { OrgListPolicy } from '@/models/user'

const BLANK = <span className="text-muted-foreground">—</span>

interface PolicyBlocksProps {
  policies: OrgListPolicy[]
  block: (policy: OrgListPolicy) => ReactNode
}

/** One fixed-height block per policy, in every column alike, so a policy's dates sit level with it. */
function PolicyBlocks({ policies, block }: PolicyBlocksProps) {
  return (
    <div className="flex flex-col gap-2">
      {policies.map((policy) => (
        <div key={policy.id} className="h-9">
          {block(policy)}
        </div>
      ))}
    </div>
  )
}

interface PolicyDateCellProps {
  policies: OrgListPolicy[]
  date: (policy: OrgListPolicy) => string | null
}

function PolicyDateCell({ policies, date }: PolicyDateCellProps) {
  return (
    <TableCell className="tabular-nums">
      <PolicyBlocks
        policies={policies}
        block={(policy) => {
          const value = date(policy)
          return value ? formatDate(value) : BLANK
        }}
      />
    </TableCell>
  )
}

function PolicySummary({ policy }: { policy: OrgListPolicy }) {
  const coverages = policy.coverageTypes.map((coverageType) => COVERAGE_TYPE_LABELS[coverageType]).join(', ')
  return (
    <>
      <div>{policy.vendorName || 'Carrier not set'}</div>
      {/* Three or four lines can outrun the column; the title keeps them all. */}
      <div className="text-muted-foreground max-w-56 truncate text-xs" title={coverages}>
        {coverages}
      </div>
    </>
  )
}

/** A customers-list row's Policies, Bound, Starts and Expires cells. */
export function CustomerPolicyCells({ policies }: { policies: OrgListPolicy[] }) {
  if (policies.length === 0) {
    return (
      <>
        <TableCell>{BLANK}</TableCell>
        <TableCell />
        <TableCell />
        <TableCell />
      </>
    )
  }
  return (
    <>
      <TableCell>
        <PolicyBlocks policies={policies} block={(policy) => <PolicySummary policy={policy} />} />
      </TableCell>
      <PolicyDateCell policies={policies} date={(policy) => policy.boundDate} />
      <PolicyDateCell policies={policies} date={(policy) => policy.dateStart} />
      <PolicyDateCell policies={policies} date={(policy) => policy.dateEnd} />
    </>
  )
}
